import { Box } from '@ethings/ui-toolkit';
import { ComponentProps, FC, JSX, CSSProperties } from 'react';
import { useExecuteQueryByWidgetId, useGetWidgetModel, WidgetById, type WidgetByIdProps } from 'sisense-sdk-ui-next/core';
import { DynamicDataGrid } from '../dynamic-data-grid';
import { useBoolFlag } from '../flags';

interface SisenseColumn {
  id?: string;
  name: string;
  type: string;
}

interface SisenseDataGridProps {
  data: { columns?: SisenseColumn[]; rows?: SisenseRow[] } | undefined;
  height: number | string;
}

interface SisenseTextWidgetProps {
  widget: {
    styleOptions?: {
      bgColor?: string;
      html?: string;
      textAlign?: string;
      vAlign?: string;
    };
  };
  height: number | string;
}

interface SisenseTextStyleOptions {
  bgColor?: string;
  html?: string;
  textAlign?: 'left' | 'center' | 'right' | string;
  vAlign?: 'valign-top' | 'valign-middle' | 'valign-bottom' | string;
}

interface SisenseTextWidgetModel {
  widgetType: 'text';
  styleOptions?: SisenseTextStyleOptions;
}

type DataGridProps = ComponentProps<typeof DynamicDataGrid>;
type DataGridColumn = NonNullable<DataGridProps['columns']>[number];
type DataGridRow = NonNullable<DataGridProps['rows']>[number];
type SisenseRow = Array<{ data: unknown; text?: string; blur?: boolean; color?: string }>;

const toDataGridColumns = (columns: SisenseColumn[] | undefined): DataGridColumn[] =>
  (columns ?? []).map((column, index) => ({
    field: column.name || column.id || `col-${index}`,
    headerName: column.name || column.id || `Column ${index + 1}`,
    flex: 1,
  }));

const toDataGridRows = (rows: SisenseRow[] | undefined, columns: SisenseColumn[] | undefined): DataGridRow[] => {
  if (!rows || !columns) { return []; }

  return rows.map((row, rowIndex) =>
    columns.reduce<Record<string, unknown>>((acc, column, columnIndex) => {
      acc.id = rowIndex;
      acc[column.name || column.id || `col-${columnIndex}`] = row[columnIndex]?.text ?? row[columnIndex]?.data ?? '';
      return acc;
    }, {}),
  );
};

const SisenseDataGrid: FC<SisenseDataGridProps> = ({ data, height }) => (
  <Box height={height}>
    <DynamicDataGrid
      autoPageSize
      columns={toDataGridColumns(data?.columns)}
      rows={toDataGridRows(data?.rows, data?.columns)}
    />
  </Box>
);

const getAlignItems = (vAlign?: string): CSSProperties['alignItems']  => {
  switch (vAlign) {
    case 'valign-top':
      return 'flex-start';
    case 'valign-bottom':
      return 'flex-end';
    case 'valign-middle':
    default:
      return 'center';
  }
};

const getJustifyContent = (textAlign?: string): CSSProperties['alignItems']  => {
  switch (textAlign) {
    case 'left':
      return 'flex-start';
    case 'right':
      return 'flex-end';
    case 'center':
    default:
      return 'center';
  }
};

const SisenseTextWidget: FC<SisenseTextWidgetProps> = ({ widget, height }) => {
  const styleOptions = widget.styleOptions ?? {};

  return (
    <Box
      height={height}
      minHeight={70}
      width="100%"
      bgcolor={styleOptions.bgColor ?? 'transparent'}
      display="flex"
      alignItems={getAlignItems(styleOptions.vAlign)}
      justifyContent={getJustifyContent(styleOptions.textAlign)}
      boxSizing="border-box"
    >
      <Box
        width='100%'
        textAlign={styleOptions.textAlign as 'left' | 'center' | 'right' | undefined}
        dangerouslySetInnerHTML={{ __html: styleOptions.html ?? '' }}
      />
    </Box>
  );
};

export const SisenseWidget: FC<WidgetByIdProps & { height: number | string }> = (props): JSX.Element => {
  const composeSdkEnabled = useBoolFlag('portals-sisense-compose-sdk-feature');

  const { data, isLoading } = useExecuteQueryByWidgetId({
    dashboardOid: props.dashboardOid,
    widgetOid: props.widgetOid,
    filters: props.filters,
  });

  const { widget } = useGetWidgetModel({
    dashboardOid: props.dashboardOid,
    widgetOid: props.widgetOid,
  });

  if (!composeSdkEnabled || !widget) return <></>;

  const shouldRenderDataGrid =
    widget.widgetType === 'pivot' ||
    (widget.widgetType === 'chart' && widget.chartType === 'table');

  if (shouldRenderDataGrid) {
    if (isLoading || !data) return <></>;
    return <SisenseDataGrid data={data} height={props.height} />;
  }

  if (widget.widgetType === 'text') {
    return <SisenseTextWidget widget={widget as SisenseTextWidgetModel} height={props.height} />;
  }

  return <WidgetById {...props} />;
};
